import { ArrowUpRight } from 'lucide-react';

/* Zwei Abschnitte, die zusammengehoeren.

   Der Beweis steht bewusst weit oben: Der groesste Wettbewerber im Markt
   behauptet woertlich denselben Anspruch ("wir messen, welche Massnahme zu
   echten Behandlungen fuehrt"). Ein behaupteter Unterschied ist damit
   wertlos - erst die sichtbare Auswertung macht ihn echt.

   Die Referenzkarten tragen jeweils eine Metrik als Ueberschrift, nicht das
   Zitat. Nach Hartley steigt die Kaufabsicht ab fuenf sichtbaren
   Erfolgsgeschichten deutlich, und Glaubwuerdigkeit muss visuell sein. */

const kohorte = [
  { monat: 'April', beratungen: 21, behandlungen: 4, quote: 19.0 },
  { monat: 'Mai', beratungen: 24, behandlungen: 4, quote: 16.7 },
  { monat: 'Juni', beratungen: 15, behandlungen: 3, quote: 20.0 },
  { monat: 'Juli', beratungen: 13, behandlungen: 5, quote: 38.5 },
];

const referenzen = [
  {
    bild: '/images/Referenzen/ricarda.webp',
    alt: 'Dr. med. Ricarda Granes, Hauptstadt Clinic Berlin: Neben Orthopädie und Ästhetik führe ich heute 5 zusätzliche OPs im Monat durch.',
  },
  {
    bild: '/images/Referenzen/andjela.webp',
    alt: 'Andjela Milosevic, AM Aesthetic Lips: 80 Prozent der Termine kommen direkt über Online-Buchungen. Meine Kunden buchen selbst, auch abends und am Wochenende, und ich muss nicht mehr hinterhertelefonieren.',
  },
  {
    bild: '/images/Referenzen/petra.webp',
    alt: 'Petra Schmidt, PS Medical Aesthetics: Man will das ja selbst können. Aber irgendwann wächst es einem über den Kopf. Das soll jemand machen, der das gelernt hat.',
  },
  {
    bild: '/images/Referenzen/fatissa.webp',
    alt: 'Fatissa Keller, Fatissa Skin Basel: Ihr wart immer für mich erreichbar und hattet immer eine Lösung für meine Probleme. Meine Vorgänger hatten damit Mühe.',
  },
  {
    bild: '/images/Referenzen/irina.webp',
    alt: 'Irina Reimer, Beautylix Cosmetics Köln: Man kann sich darauf verlassen, dass sofort geantwortet wird und nichts vergessen geht.',
  },
  {
    bild: '/images/Referenzen/fadime.webp',
    alt: 'Fadime Yigit-Mizik, Schönexklusiv Kosmetikinstitut Ingolstadt: Ihr habt mir alles eingerichtet, bis hin zu den E-Mail-Adressen. Deswegen komme ich auch immer wieder zurück.',
  },
];

export function EvidenceSection() {
  return (
    <section id="beweis" className="bg-[#F2F1EF] px-5 py-16 sm:px-6 md:px-10 md:py-24 lg:px-14" data-testid="evidence-section">
      <div className="mx-auto w-full max-w-[1180px]" data-einblenden>
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#CC3366]">Der Nachweis</p>
          <h2 className="mt-4 text-[30px] font-bold leading-[1.04] tracking-[-0.035em] text-[#0D2249] sm:text-[38px] md:text-[48px]">
            Rentiert sich Ihre Werbung? Wir rechnen es in Behandlungen, nicht in Anfragen.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#3B4962] md:text-lg">
            Eine Agentur sieht die Anfrage und danach nichts. Eine Praxissoftware sieht den Termin,
            aber nicht, wo er herkam. Wir sehen beides, weil die Buchung bei uns liegt.
          </p>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,.85fr)]">
          <div className="overflow-hidden rounded-[28px] border border-[#0D2249]/10 bg-white shadow-[0_28px_70px_-48px_rgba(13,34,73,.6)]">
            <div className="border-b border-[#E1E5EC] px-6 py-5">
              <p className="text-sm font-medium text-[#0D2249]">Beratungskonversion einer betreuten Praxis</p>
              <p className="mt-1 text-xs text-[#68748A]">
                Anteil der Beratungen, aus denen binnen 90 Tagen eine Behandlung wurde
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F2F1EF] text-left text-[11px] uppercase tracking-normal sm:text-xs sm:tracking-wider text-[#68748A]">
                    <th className="px-2.5 py-3 font-medium sm:px-6">Monat</th>
                    <th className="px-2.5 py-3 font-medium sm:px-6">Beratungen</th>
                    <th className="px-2.5 py-3 font-medium sm:px-6">Behandlungen</th>
                    <th className="px-2.5 py-3 font-medium sm:px-6">Quote</th>
                  </tr>
                </thead>
                <tbody>
                  {kohorte.map((zeile) => (
                    <tr key={zeile.monat} className="border-t border-[#F7E8EE]">
                      <td className="px-2.5 py-3.5 text-[#0D2249] sm:px-6">{zeile.monat}</td>
                      <td className="px-2.5 py-3.5 tabular-nums text-[#3B4962] sm:px-6">{zeile.beratungen}</td>
                      <td className="px-2.5 py-3.5 tabular-nums text-[#3B4962] sm:px-6">{zeile.behandlungen}</td>
                      <td className={`px-2.5 py-3.5 tabular-nums font-semibold sm:px-6 ${zeile.quote >= 30 ? 'text-[#CC3366]' : 'text-[#0D2249]'}`}>
                        {zeile.quote.toLocaleString('de-DE', { minimumFractionDigits: 1 })} %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="border-t border-[#F7E8EE] px-6 py-4 text-xs leading-relaxed text-[#68748A]">
              Echte Zahlen aus dem laufenden Betrieb, anonymisiert. Die Praxis kannte diesen Wert vorher nicht.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="relative overflow-hidden rounded-[28px] bg-[#0D2249] p-6 text-white md:p-8">
              <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full border-[28px] border-[#CC3366]/20" aria-hidden="true" />
              <p className="relative text-xs font-semibold uppercase tracking-[0.14em] text-[#F3A7C1]">Was daran neu ist</p>
              <p className="relative mt-3 text-[19px] leading-relaxed text-white">
                Die meisten Praxen messen, was eine Anfrage kostet. Fast keine weiß,
                <span className="font-semibold"> welcher Teil davon je auf dem Behandlungsstuhl ankommt.</span>
              </p>
              <p className="relative mt-4 text-[15px] leading-relaxed text-white/65">
                Genau dort liegt das Geld: Von 21 Beratungen im April wurden 4 zur Behandlung.
                Die anderen 17 saßen bereits in der Praxis.
              </p>
            </div>
            <a
              href="#top"
              className="inline-flex items-center justify-between gap-2 rounded-[22px] border border-[#0D2249]/10 bg-white px-6 py-5 text-[15px] font-semibold text-[#0D2249] transition hover:-translate-y-0.5 hover:border-[#CC3366]"
            >
              Für Ihre Praxis durchrechnen
              <ArrowUpRight className="h-4 w-4 text-[#CC3366]" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ReferenceWall() {
  return (
    <section id="referenzen" className="px-5 py-16 sm:px-6 md:px-10 md:py-24 lg:px-14" data-testid="reference-wall">
      <div className="mx-auto w-full max-w-[1180px]" data-einblenden>
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#CC3366]">Sechs Praxen</p>
          <h2 className="mt-4 text-[30px] font-bold leading-[1.04] tracking-[-0.035em] text-[#0D2249] sm:text-[38px] md:text-[48px]">
            Fragen Sie nicht uns. Fragen Sie die Praxen, die damit arbeiten.
          </h2>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2" data-folge>
          {referenzen.map((ref) => (
            <figure key={ref.bild} className="group overflow-hidden rounded-[26px] border border-[#0D2249]/8 bg-white shadow-[0_22px_60px_-45px_rgba(13,34,73,.5)]">
              <img
                src={ref.bild}
                alt={ref.alt}
                loading="lazy"
                width="1400"
                height="1056"
                className="block h-auto w-full transition duration-500 group-hover:scale-[1.015]"
              />
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
